// cardVisuals.ts
//
// BETA 16.31 — Shared visuals for level-up / shrine / chest pick cards.
//
// Three concerns live here:
//   1. Rarity-tinted FIRE GLOW around card borders, with flame radius scaled
//      by rarity tier. Per Dylan: "the more legendary the item is, like
//      common rare epic, the ladder of the better items, the flames should
//      be shorter on the common and highest on legendaries. and follow that
//      rise in flame for those types."
//   2. STAT EMOJI mapping that matches the on-screen left-stats panel
//      (renderLeftStatsPanel in engine.ts). Per Dylan: "use the same emojies
//      we use in stats on screen for the buffs beside the word. example:
//      luck should have the 4 leaf clover emoji on the left of the word luck."
//   3. A small text-prefix helper that takes a stat label and prepends the
//      matching emoji.
//
// Caller is responsible for filling the card background BEFORE invoking
// drawCardFireGlow — this function only adds the glow + final border stroke.

export type RarityTier =
  | 'common'
  | 'uncommon'
  | 'rare'
  | 'epic'
  | 'legendary'
  | 'negative';

interface FlameProfile {
  /** Outer halo blur radius (px) — biggest glow ring */
  outer: number;
  /** Mid flame blur radius (px) */
  mid: number;
  /** Hot core blur radius (px) — tightest, brightest */
  inner: number;
  /** Outer halo color (low alpha, soft) */
  outerColor: string;
  /** Mid flame color (warmer, more saturated) */
  midColor: string;
}

/**
 * Tier-scaled flame profiles. Outer-radius ladder (BETA 16.32 retune —
 * Dylan: "still no flames"):
 *   common (18) < uncommon (28) < rare (44) < epic (62) < legendary (90)
 *
 * That ratio (1×, 1.5×, 2.4×, 3.4×, 5×) puts legendary at ~5× common's
 * flame height while keeping common BIG ENOUGH TO ACTUALLY SEE.
 *
 * Shadow colors are now full-opacity (1.0) so the canvas-shadow renderer
 * gets a saturated source to feather out — previous 0.35-0.6 alpha values
 * produced glow that was invisible on the dark backdrop. Brightness is
 * controlled by blur radius, not by alpha.
 *
 * Negative shrine cards get a red-fire profile sized between rare/epic.
 */
export const FLAME_PROFILES: Record<RarityTier, FlameProfile> = {
  common: {
    outer: 18, mid: 11, inner: 5,
    outerColor: 'rgba(120,255,120,1.0)',
    midColor:   'rgba(180,255,140,1.0)',
  },
  // BETA 20.28 — flame-glow colors were leftover from an OLD rarity scheme
  // (rare=blue, epic=orange, legendary=magenta). Recolored to match the live
  // 3-tier palette: uncommon→green, rare/epic→purple, legendary→gold. Flame
  // SIZE ladder (common small → legendary huge) is kept for visual hierarchy.
  uncommon: {
    outer: 28, mid: 17, inner: 7,
    outerColor: 'rgba(120,255,120,1.0)',
    midColor:   'rgba(180,255,140,1.0)',
  },
  rare: {
    outer: 44, mid: 26, inner: 10,
    outerColor: 'rgba(150,90,255,1.0)',
    midColor:   'rgba(200,150,255,1.0)',
  },
  epic: {
    outer: 62, mid: 38, inner: 14,
    outerColor: 'rgba(150,90,255,1.0)',
    midColor:   'rgba(200,150,255,1.0)',
  },
  legendary: {
    outer: 90, mid: 56, inner: 20,
    outerColor: 'rgba(255,200,50,1.0)',
    midColor:   'rgba(255,230,140,1.0)',
  },
  negative: {
    outer: 38, mid: 22, inner: 9,
    outerColor: 'rgba(255,60,60,1.0)',
    midColor:   'rgba(255,120,100,1.0)',
  },
};

/**
 * Stat emoji map — KEYS ARE LOWERCASE-ALPHA-ONLY (no spaces / punctuation).
 * Matches the icons in renderLeftStatsPanel (engine.ts ~5274+).
 *
 * Lookup uses getStatEmoji(label) which lowercases + strips non-alpha,
 * then tries direct match first, then prefix match.
 */
export const STAT_EMOJI: Record<string, string> = {
  hp: '❤',                 // ❤ heart
  hpregen: '✚',            // ✚ plus
  regen: '✚',              // ✚

  damage: '⚔',             // ⚔ swords
  atkspeed: '⚡',           // ⚡ bolt
  attackspeed: '⚡',
  reload: '⚡',             // reload is "atk-speed-y"

  critchance: '✸',         // ✸ asterisk-burst
  crit: '✸',
  critdamage: '💥',   // 💥 collision
  critdmg: '💥',

  lifesteal: '🩸',    // 🩸 droplet of blood
  armor: '🛡',        // 🛡 shield (item armor uses this)
  shield: '◈',             // ◈ shield diamond
  shieldregen: '◈',        // shield regen — same shield diamond
  movespeed: '👟',    // 👟 sneaker
  speed: '👟',
  luck: '🍀',         // 🍀 four-leaf clover
  evasion: '💨',      // 💨 dash-cloud
  thorns: '🌵',       // 🌵 cactus
  elitedmg: '👑',     // 👑 crown
  elitedamage: '👑',

  bounces: '↺',            // ↺ counterclockwise arrow
  bounce: '↺',
  projectiles: '⋙',        // ⋙ very greater
  projectile: '⋙',

  knockback: '↗',          // ↗ northeast arrow
  attackarea: '⊙',         // ⊙ circled dot
  size: '⊙',
  area: '⊙',
  pickuprange: '◎',        // ◎ bullseye
  pickup: '◎',
  range: '◎',

  xpgain: '★',             // ★ star
  xp: '★',
  xpbonus: '★',
  goldgain: '◇',           // ◇ diamond
  gold: '◇',
  goldbonus: '◇',

  cooldown: '⏱',           // ⏱ stopwatch
  pierce: '➞',             // ➞ heavy arrow (chest item key)
  drops: '🍌',        // 🍌 banana — playful catch-all for "drops"
};

/** Strip non-alpha + lowercase. */
function _norm(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z]/g, '');
}

/**
 * Find the emoji for a stat label like "Damage", "Move Speed", "Crit Chance",
 * "Reload", "Pickup Range", etc. Returns '' if no match.
 */
export function getStatEmoji(label: string): string {
  if (!label) return '';
  const norm = _norm(label);
  if (STAT_EMOJI[norm]) return STAT_EMOJI[norm];
  // Prefix match — "damageX" still hits "damage"
  for (const key of Object.keys(STAT_EMOJI)) {
    if (norm.startsWith(key)) return STAT_EMOJI[key];
  }
  // Suffix match — "bonusxp" etc.
  for (const key of Object.keys(STAT_EMOJI)) {
    if (norm.endsWith(key)) return STAT_EMOJI[key];
  }
  return '';
}

/** Returns the FlameProfile for a rarity. Defaults to 'common' if unknown. */
export function getFlameProfile(rarity: string): FlameProfile {
  const r = (rarity || '').toLowerCase() as RarityTier;
  return FLAME_PROFILES[r] ?? FLAME_PROFILES.common;
}

type RoundRectFn = (
  c: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) => void;

/**
 * Draws a 3-pass fire-glow border around a rounded-rect card.
 *
 * BETA 16.32 retune — Dylan reported "still no flames" after the 16.31
 * ship. Root cause of the invisible glow:
 *   1. Pass 1/2 used low-alpha shadowColor (0.35-0.6). Canvas shadows
 *      inherit color FROM shadowColor — low alpha there = faint shadow.
 *   2. Each pass also stroked a visible line with the same low-alpha
 *      color, creating distracting overlay rings without glow weight.
 *
 * New approach: each outer/mid pass paints the SHADOW only (with full-
 * opacity bright color in shadowColor) by stroking with a 0-width stroke
 * at full alpha. Canvas honors shadow on the stroke even when the stroke
 * itself isn't visible. The hot-core pass is the only visible-line pass.
 *
 * Result: the dark border has a chunky concentric glow halo around it
 * scaled by rarity tier. Common still pops; legendary roars.
 *
 * Flames flicker over time and scale by rarity tier.
 *
 * @param emphasize  When true (selected/hovered), boost intensity ~1.3×
 * @param time       Animation time (use performance.now())
 * @param seed       Per-card variation seed (e.g. card index)
 */
export function drawCardFireGlow(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  radius: number,
  rarity: string,
  borderCol: string,
  emphasize: boolean,
  time: number,
  seed: number,
  roundRectFn: RoundRectFn,
): void {
  const profile = getFlameProfile(rarity);
  const flicker = 0.90 + 0.10 * Math.sin(time / 140 + seed * 1.7);
  const intensity = (emphasize ? 1.25 : 1.0) * flicker;

  ctx.save();

  // BETA 17.40 — cycle-30aq perf: chest_spin overlay was dropping FPS
  // 60 → 51 because this function ran 8 strokes-with-shadowBlur per
  // landed card per frame (3 cards × 8 = 24 expensive shadow passes/
  // frame just on the glow). Removed the 3 "double-up for warmth"
  // duplicate strokes. Glow intensity drops ~30%, but the OUTER halo
  // and HOT CORE are still drawn — visually still distinct per rarity.
  // For a heavier offscreen-canvas cache fix, see future cycle.

  // Pass 1 — OUTER HALO. Big blur, OUTER color.
  // Stroke is invisible (lineWidth 0.5 with rarity color at full alpha;
  // the line itself is too thin to see, but the canvas shadow renderer
  // still paints the full halo around the path).
  roundRectFn(ctx, x, y, w, h, radius);
  ctx.strokeStyle = profile.outerColor;
  ctx.shadowColor = profile.outerColor;
  ctx.shadowBlur = profile.outer * intensity;
  ctx.lineWidth = 0.5;
  ctx.stroke();
  // (Removed second outer pass — 50% perf saving)

  // Pass 2 — MID FLAME. Smaller blur, hotter color closer to white.
  ctx.shadowColor = profile.midColor;
  ctx.shadowBlur = profile.mid * intensity;
  ctx.strokeStyle = profile.midColor;
  ctx.stroke();
  // (Removed second mid pass — 50% perf saving)

  // Pass 3 — HOT CORE. Visible border stroke in the caller's accent
  // color, with tight bright glow.
  ctx.shadowColor = borderCol;
  ctx.shadowBlur = profile.inner * intensity;
  ctx.strokeStyle = borderCol;
  ctx.lineWidth = emphasize ? 3.5 : 2.5;
  ctx.stroke();
  // (Removed re-stroke — 50% perf saving)

  ctx.restore();
}

/** Convenience: prefix `label` with its stat emoji + a space, or return label
 *  unchanged if no emoji match. */
export function prefixStatEmoji(label: string): string {
  const emoji = getStatEmoji(label);
  return emoji ? `${emoji} ${label}` : label;
}

/**
 * BETA 16.32 — Inject stat emojis into stat-list descriptions on chest
 * cards. Per Dylan: "use the same emojies we use in stats on screen for
 * the buffs beside the word."
 *
 * Matches "+N stat" / "-N stat" / "+N% stat" patterns inside a longer
 * string and prefixes each detected stat name with its emoji.
 *
 * Examples:
 *   "+20 luck, +4% crit chance"      → "+20 🍀 luck, +4% ✸ crit chance"
 *   "+5 HP regen per second"         → "+5 ✚ HP regen per second"
 *   "Active item (Q): stuns enemies" → unchanged (no stat-pattern match)
 *
 * Pattern explanation:
 *   ([+\-]\d+(?:\.\d+)?%?\s+)   — number with optional decimal/%/sign
 *   ([A-Za-z][A-Za-z\s]*?)      — stat-name capture (lazy)
 *   (?=,|\.|\bper\b|$)          — stop at comma/period/"per"/end
 */
export function injectStatEmojis(text: string): string {
  if (!text) return text;
  return text.replace(
    /([+\-]\d+(?:\.\d+)?%?\s+)([A-Za-z][A-Za-z\s]*?)(?=[,.]|\bper\b|$)/g,
    (_full, num, stat) => {
      const rawStat = String(stat);
      const trimmed = rawStat.trim();
      // BETA 19.6 — Preserve the trailing whitespace that the regex captured
      // before the lookahead. Without this, the .trim() drops a space and
      // the next word ("per", "armor", etc.) butts up against the stat name:
      //   "+5 HP regen per second"  →  "+5 ❤ HP regenper second"   (bug)
      //   "+5 HP regen per second"  →  "+5 ❤ HP regen per second"  (fix)
      const trailingWs = rawStat.length > trimmed.length ? rawStat.slice(trimmed.length) : '';
      const emoji = getStatEmoji(trimmed);
      return emoji ? `${num}${emoji} ${trimmed}${trailingWs}` : `${num}${trimmed}${trailingWs}`;
    },
  );
}
